<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      基本情報管理
    </Header>

    <RejectionReason
      v-if="isRejected"
      :details="rejectionReasonDetails"
      :comment="rejectionReasonComment"
    />

    <CustomValidationObserver
      tag="div"
      class="my-4 mx-3"
      v-slot="{ handleSubmit }"
      :initialValues="formValues"
      ref="vObserver"
    >
      <PersonalInfo
        v-model="inputValues"
        :isUnderExamination="isUnderExamination"
        :fromRegister="false"
      />
      <AppButton
        block
        class="mt-4"
        @click="handleSubmit(onClick)"
        :disabled="isUnderExamination"
      >
        {{ buttonLabel }}
      </AppButton>
    </CustomValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import PersonalInfo from "@/components/organisms/common/PersonalInfo.vue";
import Header from "@/components/organisms/common/Header.vue";
import RejectionReason from "@/components/atoms/other/RejectionReason.vue";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { authStore } from "@/store/auth";
import VerifiedStatus from "@/models/verifiedStatus";
import { accountService } from "@/app/services/AccountService";
import {
  RequestVerificationQueueBody,
  PersonalInfoQueueBody
} from "@/types/user/RequestVerificationQueueBody";
import RejectionReasonTitle from "@/models/rejectionReasonTitle";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";
import { splitDate } from "@/utils/date";

@Component({
  components: {
    AppButton,
    FeatherIcon,
    Header,
    PersonalInfo,
    RejectionReason
  }
})
export default class MypagePersonalInfo extends Vue {
  inputValues: PersonalInfoQueueBody = {
    firstName: "",
    lastName: "",
    firstNameKana: "",
    lastNameKana: "",
    sex: "2",
    birthDate: null,
    postalCode: "",
    prefecture: null,
    locality: null,
    address1: "",
    address2: "",
    email: null,
    hasRegisteredPersonalInfo: false
  };
  rejectionReasonComment: string | null = null;
  requestQueueUpdatedAt: string | null = null;

  get user() {
    return authStore.user!;
  }

  get prefecture() {
    return (
      (this.user.prefecture || authStore.firstBootSurveyV2?.prefecture) ?? null
    );
  }

  get locality() {
    return (
      (this.user.locality || authStore.firstBootSurveyV2?.locality) ?? null
    );
  }

  get formValues() {
    const [birthDateYear, birthDateMonth, birthDateDay] = splitDate(
      this.user.birthDate,
      "-"
    );

    return {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      firstNameKana: this.user.firstNameKana,
      lastNameKana: this.user.lastNameKana,
      sex: this.user.sex,
      birthDateYear,
      birthDateMonth,
      birthDateDay,
      email: this.user.email,
      postalCode: this.user.postalCode,
      prefecture: this.prefecture,
      locality: this.locality,
      address1: this.user.address1,
      address2: this.user.address2,
      workerService: this.user.workerService
    };
  }

  get isRejected() {
    return (
      this.user.verifiedStatus === VerifiedStatus.REJECTED &&
      this.user.rejectionReason.title === RejectionReasonTitle.PERSONAL_INFO
    );
  }

  get rejectionReasonDetails() {
    return this.user.rejectionReason.details;
  }

  get isUnderExamination() {
    if (!this.hasRegisteredPersonalInfo) return false;
    if (!this.hasRegisteredProfile) return false;
    if (this.user.verifiedStatus === VerifiedStatus.UNVERIFIED) {
      return true;
    }
    return false;
  }

  get hasRegisteredPersonalInfo() {
    return authStore.hasRegisteredPersonalInfo;
  }

  get hasRegisteredProfile() {
    return this.user.hasRegisteredProfile;
  }

  get buttonLabel() {
    if (this.isUnderExamination) return "審査中は変更できません";
    if (this.hasRegisteredProfile) return "保存する";
    return "保存する";
  }

  async created() {
    try {
      this.$loading.show();
      await this.initInput();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  mounted() {
    // 取得した値でフォームをリセットしておく
    // https://github.com/logaretm/vee-validate/issues/4602
    (this.$refs.vObserver as InstanceType<
      typeof CustomValidationObserver
    >).resetForm(this.formValues);
  }

  async initInput() {
    const requestQueue = await accountService.fetchLatestRequestVerificationQueue();
    this.rejectionReasonComment = requestQueue?.comment || null;
    this.requestQueueUpdatedAt = requestQueue?.updatedAt;
    this.setInputValues(requestQueue);
  }

  setInputValues(user: RequestVerificationQueueBody | null) {
    this.inputValues = Object.fromEntries(
      Object.keys(this.inputValues).map(k => {
        const key = k as keyof typeof this.user;
        // @ts-ignore
        const value = user && user[key] !== null ? user[key] : this.user[key];
        return [key, value];
      })
    ) as PersonalInfoQueueBody;
    // 都道府県や市区町村の値がない場合は FirstBootSurvey での入力値を使う
    this.inputValues.prefecture =
      this.inputValues.prefecture ?? this.prefecture;
    this.inputValues.locality = this.inputValues.locality ?? this.locality;
  }

  async onClick() {
    try {
      this.$loading.show();
      const body = {
        ...this.inputValues,
        hasRegisteredPersonalInfo: true,
        uuid: authStore.sessionId,
        updatedAt: this.requestQueueUpdatedAt
      };
      if (this.hasRegisteredPersonalInfo && this.hasRegisteredProfile) {
        // 初回登録済みのユーザーの場合requestQueueを作成or更新
        await accountService.requestVerificationQueue(body);
      } else {
        // 初回登録がまだのユーザーの場合userDetailを更新
        await authStore.registerUserInfo(body);
      }
      await this.$router.push({ name: "RegistrationInformationAndStatus" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  onClickBack() {
    this.$router.back();
  }
}
</script>
